/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useCallback, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { matchPath, useLocation, useParams } from 'react-router-dom'
import { css } from '@emotion/react'

import {
  EuiIcon,
  useEuiTheme,
  EuiSideNav,
  EuiCollapsibleNavBeta,
  EuiTourStep,
  EuiFlexItem,
  EuiFlexGroup,
  EuiText,
} from '@elastic/eui'

import Feature from '@modules/utils/feature'
import type { EuiSideNavItem } from '@modules/utils/navigation'
import { createItem } from '@modules/utils/navigation'
import { buildProjectsMenu } from '@modules/project-user-pages/navigation'
import { startingWith } from '@modules/routing/startingWith'
import { buildNetworkSecurityMenu } from '@modules/network-security-pages/navigation'
import { useConnectedClustersMenu } from '@modules/ccm-user-lib/navigation'
import { useProfile } from '@modules/profile-lib/hooks'
import usePermissions, { usePermissionsBatch } from '@modules/permissions-lib/hooks'
import { useLocalStorage } from '@modules/utils/hooks/useLocalStorage'
import LocalStorageKey from '@modules/utils/localStorageKeys'

import { hasSizedSliderResource, getVersion } from '@/lib/stackDeployments/selectors/fundamentals'
import { isSliderSupportedForDeployment } from '@/lib/stackDeployments/selectors/deploymentTemplates'
import { getDisplayName } from '@/lib/stackDeployments/selectors/display'
import { getSupportedSliderInstanceTypesWithoutEs } from '@/lib/sliders/support'
import { getSliderPrettyName } from '@/lib/sliders/messages'

import { getHerokuCluster } from '../../../../lib/heroku'
import {
  clusterSnapshotUrl,
  deploymentExtensionCreateUrl,
  deploymentExtensionsUrl,
  deploymentExtensionUrl,
  deploymentsUrl,
  deploymentUrl,
  deploymentUrls,
  accessSecurityUrl,
  supportUrl,
  logsMonitoringUrl,
} from '../../../../lib/urlBuilder'
import {
  accountContactsUrl,
  accountMembersUrl,
  accountUrl,
  apiKeysUrl,
  billingUrl,
  organizationSecurityUrl,
  trafficFiltersUrl,
  trustManagementUrl,
} from '../../urls'
import { isPortalUrl } from '../../../../lib/portal'
import {
  getDeploymentHealthProblems,
  getHealthStatusNavigationIcon,
} from '../../../../lib/healthProblems'
import { isFeatureActivated } from '../../../../store'

import NavLinkItem from './NavLinkItem'
import messages from './navigationMessages'

import type { AllProps } from './types'

type RouteParams = {
  regionId: string
  deploymentId: string
  snapshotName: string
  extensionId: string
}

const UserconsoleChromeNavigation = (props: AllProps) => {
  const [isSideNavBodyOpenOnMobile, setIsSideNavBodyOpenOnMobile] = useState(false)
  const [isSideNavFooterOpenOnMobile, setIsSideNavFooterOpenOnMobile] = useState(false)
  const { pathname } = useLocation()

  const {
    deployment,
    deploymentTemplate,
    stackDeployment,
    eolStatus,
    health,
    deploymentHealthRequest,
    showHelpPage,
    isHeroku,
    regionId,
    fetchDeploymentTemplates,
  } = props

  const { euiTheme } = useEuiTheme()

  const stackVersion = stackDeployment
    ? getVersion({ deployment: stackDeployment })
    : deployment?.plan.version

  useEffect(() => {
    if (!isDeploymentActive(pathname)) {
      return
    }

    if (!regionId || !stackVersion) {
      return
    }

    fetchDeploymentTemplates({ regionId, stackVersion, showMaxZones: true })
  }, [regionId, stackVersion])

  useEffect(() => {
    onNavigate(pathname)
  }, [pathname])

  return (
    <EuiCollapsibleNavBeta
      isCollapsed={false}
      css={css`
        background: ${euiTheme.colors.backgroundBaseSubdued};
      `}
    >
      <ResourcesNavigation
        isHeroku={isHeroku}
        deployment={deployment}
        stackDeployment={stackDeployment}
        eolStatus={eolStatus}
        health={health}
        deploymentHealthRequest={deploymentHealthRequest}
        deploymentTemplate={deploymentTemplate}
        isSideNavOpenOnMobile={isSideNavBodyOpenOnMobile}
        toggleOpenOnMobile={toggleOpenOnMobile}
      />
      <ManagementNavigation
        isHeroku={isHeroku}
        isSideNavOpenOnMobile={isSideNavFooterOpenOnMobile}
        toggleOpenMobile={toggleOpenOnMobile}
        showHelpPage={showHelpPage}
      />
    </EuiCollapsibleNavBeta>
  )

  function toggleOpenOnMobile() {
    setIsSideNavBodyOpenOnMobile((prevState) => !prevState)
    setIsSideNavFooterOpenOnMobile((prevState) => !prevState)
  }

  function onNavigate(newPath: string) {
    if (!isPortalUrl(newPath)) {
      setIsSideNavBodyOpenOnMobile(false)
      setIsSideNavFooterOpenOnMobile(false)
    }
  }
}

export default UserconsoleChromeNavigation

const ResourcesNavigation = ({
  isHeroku,
  deployment,
  stackDeployment,
  eolStatus,
  health,
  deploymentHealthRequest,
  deploymentTemplate,
  isSideNavOpenOnMobile,
  toggleOpenOnMobile,
}) => {
  const { formatMessage } = useIntl()
  const { pathname } = useLocation()
  const params = useParams<RouteParams>()
  const { snapshotName, deploymentId } = params

  const connectedClustersMenu = useConnectedClustersMenu()
  const deploymentsActive = isDeploymentActive(pathname)
  const depth = 1

  const deploymentItems = deploymentsActive
    ? deploymentNavigation({
        snapshotName,
        deploymentId,
        formatMessage,
        stackDeployment,
        eolStatus,
        health,
        deploymentHealthRequest,
        pathname,
        deploymentTemplate,
        deployment,
      })
    : []

  const euiSideNavItems: EuiSideNavItem[] = []
  const { euiTheme } = useEuiTheme()

  if (isHeroku) {
    const herokuCluster = getHerokuCluster()

    if (herokuCluster) {
      euiSideNavItems.push(
        ...deploymentNavigation({
          snapshotName,
          deploymentId,
          formatMessage,
          stackDeployment,
          eolStatus,
          health,
          deploymentHealthRequest,
          pathname,
          deploymentTemplate,
          deployment,
        }),
      )
    }
  } else {
    euiSideNavItems.push(
      createItem({
        name: formatMessage(messages.hostedDeployments),
        href: deploymentsUrl(),
        depth,
        items: deploymentItems,
        [`data-test-id`]: `deployment-section-link`,
      }),
    )
  }

  if (!isHeroku) {
    euiSideNavItems.push(
      buildProjectsMenu({
        formatMessage,
        location,
      }),
    )
  }

  euiSideNavItems.push(...connectedClustersMenu)

  const isNavItemSelected = (item: EuiSideNavItem) => {
    if (!item.href) {
      return false
    }

    return Boolean(matchPath(startingWith(item.href), pathname))
  }

  const euiSideNavItemsWithSelectedItem = setSelectedItem(euiSideNavItems, isNavItemSelected)

  return (
    <EuiCollapsibleNavBeta.Body style={{ padding: `${euiTheme.size.m} ${euiTheme.size.l}` }}>
      <EuiSideNav
        isOpenOnMobile={isSideNavOpenOnMobile}
        toggleOpenOnMobile={toggleOpenOnMobile}
        mobileTitle={formatMessage(messages.resources)}
        items={euiSideNavItemsWithSelectedItem}
        renderItem={NavLinkItem}
      />
    </EuiCollapsibleNavBeta.Body>
  )
}

function deploymentNavigation({
  snapshotName,
  deploymentId,
  stackDeployment,
  deployment,
  formatMessage,
  eolStatus,
  health,
  deploymentHealthRequest,
  deploymentTemplate,
  pathname,
}) {
  const id = deploymentId!
  const urls = deploymentUrls(id)
  const version = stackDeployment ? getVersion({ deployment: stackDeployment }) : undefined

  const elasticsearchSubItems = [
    createItem({
      name: formatMessage(messages.clusterSnapshots),
      href: urls.snapshots,
      [`data-test-id`]: `deployment-index-elasticsearch-snapshots-link`,
      items: snapshotName
        ? [
            createItem({
              name: snapshotName,
              href: clusterSnapshotUrl(id, snapshotName),
            }),
          ]
        : undefined,
    }),
    createItem({
      name: formatMessage(messages.apiConsole),
      href: urls.console,
      'data-test-id': `deployment-console-link`,
    }),
  ]

  const editItem = createItem({
    name: formatMessage(messages.deploymentEdit),
    href: urls.edit,
    'data-test-id': `deployment-edit-link`,
  })

  // Compute all the deployment problems so we can figure out
  // if we should show a warning icon in the healthStatusItem
  // from the navigation bar.
  const [problems] = getDeploymentHealthProblems({
    deployment: stackDeployment!,
    eolStatus,
    health,
    deploymentHealthRequest,
  })

  const elasticsearchItems = createItem({
    name: formatMessage(messages.deploymentElasticsearch),
    href: urls.elasticsearch,
    'data-test-id': `deployment-index-elasticsearch-overview-link`,
    items: elasticsearchSubItems,
  })

  const monitoringSectionNavItems = createItem({
    name: formatMessage(messages.deploymentMonitoring),
    href: urls.healthStatus,
    iconRight: getHealthStatusNavigationIcon(problems),
    'data-test-id': `deployment-health-status`,
    items: [
      createItem({
        name: formatMessage(messages.loggingMonitoring),
        href: logsMonitoringUrl(id),
        [`data-test-id`]: `deployment-index-logging-monitoring-link`,
      }),
      createItem({
        name: formatMessage(messages.deploymentMetrics),
        href: urls.metrics,
        'data-test-id': `deployment-index-elasticsearch-metrics-link`,
      }),
    ],
  })

  const sliderItems = getSupportedSliderInstanceTypesWithoutEs()
    .filter((sliderInstanceType) =>
      isSliderSupportedForDeployment({
        deployment: stackDeployment || undefined,
        deploymentTemplate: deploymentTemplate?.deployment_template,
        sliderInstanceType,
      }),
    )
    .filter((sliderInstanceType) =>
      stackDeployment
        ? hasSizedSliderResource({
            deployment: stackDeployment,
            resourceType: sliderInstanceType,
          })
        : false,
    )
    .map((sliderInstanceType) =>
      createItem({
        name: formatMessage(getSliderPrettyName({ sliderInstanceType, version })),
        href: urls[sliderInstanceType],
        'data-test-id': `deployment-index-${sliderInstanceType}-overview-link`,
      }),
    )

  const activityItem = createItem({
    name: formatMessage(messages.deploymentActivity),
    href: urls.deploymentActivity,
    'data-test-id': `deployment-index-activity-link`,
    isSelected: pathname.startsWith(urls.deploymentActivity),
  })

  const securityItem = createItem({
    name: formatMessage(messages.deploymentSecurity),
    href: urls.security,
    [`data-test-id`]: `deployment-index-elasticsearch-security-link`,
  })

  const deploymentItems = [
    editItem,
    monitoringSectionNavItems,
    elasticsearchItems,
    ...sliderItems,
    activityItem,
    securityItem,
  ]

  return [
    createItem({
      name: getDeploymentName(),
      href: urls.root,
      'data-test-id': `deployment-index-overview-link`,
      id: `deployment-index-overview-link`,
      items: deploymentItems,
    }),
  ]

  function getDeploymentName() {
    if (stackDeployment) {
      return getDisplayName({ deployment: stackDeployment })
    }

    if (deploymentId) {
      return deploymentId.slice(0, 6)
    }

    if (deployment) {
      return deployment.displayName
    }

    return 'unknown'
  }
}

interface ManagementNavigationProps {
  isHeroku: boolean
  showHelpPage: boolean
  isSideNavOpenOnMobile?: boolean
  toggleOpenMobile?: () => void
}

const ManagementNavigation = ({
  isHeroku,
  showHelpPage,
  isSideNavOpenOnMobile,
  toggleOpenMobile,
}: ManagementNavigationProps) => {
  const { formatMessage } = useIntl()
  const { pathname } = useLocation()
  const params = useParams<RouteParams>()
  const profile = useProfile()
  const { euiTheme } = useEuiTheme()
  const [isTourOpen, setIsTourOpen] = useLocalStorage(LocalStorageKey.navTourStorageKey, true)
  const [isAccessSecurityOpen, setIsAccessSecurityOpen] = useState(true)
  const [isOrganizationOpen, setIsOrganizationOpen] = useState(false)

  const {
    hasViewMembersTabPermission,
    hasViewAccountTabPermission,
    hasViewApiKeysTabPermission,
    hasAccessToSecurityTab,
    isLoading: isLoadingPermissions,
  } = usePermissionsBatch({
    hasViewMembersTabPermission: {
      permissionsToCheck: [
        {
          type: 'organization-members',
          action: 'list',
        },
        {
          type: 'organization-invitation',
          action: 'list',
        },
      ],
      behavior: 'some',
    },
    hasViewAccountTabPermission: {
      permissionsToCheck: [
        {
          type: 'organization',
          action: 'update',
        },
      ],
    },
    hasViewApiKeysTabPermission: {
      permissionsToCheck: [
        {
          type: 'api-key',
          action: 'list',
        },
      ],
    },
    hasAccessToSecurityTab: {
      permissionsToCheck: [
        {
          type: 'organization-idp',
          action: 'get',
        },
        {
          type: 'organization-domain-claim',
          action: 'get',
        },
        {
          type: 'organization-role-mapping',
          action: 'get',
        },
      ],
      behavior: 'some',
    },
  })

  const hasNoPermissionsToSeeTabs =
    profile?.organization_id &&
    !hasViewAccountTabPermission &&
    !hasViewMembersTabPermission &&
    !hasViewApiKeysTabPermission &&
    !hasAccessToSecurityTab

  // Handle tour close and save state to localStorage
  const handleTourClose = useCallback(() => {
    setIsTourOpen(false)
  }, [setIsTourOpen])

  // Initialize navigation items and permissions
  const navItems: EuiSideNavItem[] = []
  const hasCreateBillingOrganizationPermission = usePermissions([
    { type: 'billing-organization', action: 'create' },
  ])

  const { extensionId } = params
  const creatingExtension = isDeploymentExtensionCreateActive(pathname)
  const editingExtension = isDeploymentExtensionEditActive(pathname)
  const depth = 1

  const accessSecurityItems = accessSecurityNavigation({
    formatMessage,
    depth,
  })

  const organizationItems: EuiSideNavItem[] = getOrganizationItems({
    formatMessage,
    organizationId: profile?.organization_id,
    isLoadingPermissions,
    hasViewAccountTabPermission,
    hasNoPermissionsToSeeTabs,
    hasViewMembersTabPermission,
    hasViewApiKeysTabPermission,
    hasAccessToSecurityTab,
  })

  if (!isHeroku) {
    if (profile?.organization_id !== undefined) {
      navItems.push(
        createItem({
          name: formatMessage(messages.accessSecurity),
          href: accessSecurityUrl(),
          icon: <EuiIcon type='securitySignal' size='m' />,
          items: isAccessSecurityOpen ? accessSecurityItems : [],
          depth,
          hasCaret: true,
          itemIsOpen: isAccessSecurityOpen,
          onCaretClick: () => setIsAccessSecurityOpen(!isAccessSecurityOpen),
          ['data-test-id']: 'deployment-features-index-link',
        }),
      )
    }
  }

  if (profile && (profile.allow_bundles || profile.allow_plugins)) {
    const extensionsItems = [
      ...(editingExtension && extensionId
        ? [
            createItem({
              name: extensionId,
              depth,
              href: deploymentExtensionUrl(extensionId),
              ['data-test-id']: 'deployment-features-deployment-edit-extensions-link',
            }),
          ]
        : []),
      ...(creatingExtension
        ? [
            createItem({
              name: formatMessage(messages.createExtension),
              href: deploymentExtensionCreateUrl(),
              depth,
              ['data-test-id']: 'deployment-features-deployment-create-extensions-link',
            }),
          ]
        : []),
    ]

    navItems.push(
      createItem({
        name: formatMessage(messages.extensionsOverview),
        icon: <EuiIcon type='plugs' size='m' />,
        href: deploymentExtensionsUrl(),
        items: extensionsItems,
        depth,
        ['data-test-id']: 'deployment-features-deployment-extensions-link',
      }),
    )
  }

  if (!isHeroku && profile) {
    navItems.push(
      createItem({
        name: formatMessage(messages.organization),
        href: accountUrl(),
        icon: <EuiIcon type='users' size='m' />,
        depth,
        items: isOrganizationOpen ? organizationItems : [],
        hasCaret: true,
        itemIsOpen: isOrganizationOpen,
        onCaretClick: () => setIsOrganizationOpen(!isOrganizationOpen),
        ['data-test-id']: 'deployment-organization-link',
      }),
    )
  }

  if (hasCreateBillingOrganizationPermission.hasPermissions) {
    navItems.push(
      createItem({
        name: formatMessage(messages.billingSubscription),
        icon: <EuiIcon type='visBarVertical' size='m' />,
        href: billingUrl(),
        depth,
        ['data-test-id']: 'deployment-billing-link',
      }),
    )
  }

  if (showHelpPage) {
    navItems.push(
      createItem({
        name: formatMessage(messages.support),
        icon: <EuiIcon type='help' size='m' />,
        depth,
        href: supportUrl(),
      }),
    )
  }

  // Navigation item selection logic
  const isNavItemSelected = (item: EuiSideNavItem) => {
    if (!item.href) {
      return false
    }

    return Boolean(matchPath(startingWith(item.href), pathname))
  }

  const navItemsWithSelected = setSelectedItem(navItems, isNavItemSelected)

  return (
    <EuiCollapsibleNavBeta.Footer
      style={{
        backgroundColor: 'transparent',
        padding: `${euiTheme.size.m} ${euiTheme.size.l}`,
      }}
    >
      <EuiTourStep
        isStepOpen={isTourOpen}
        minWidth={300}
        maxWidth={350}
        onFinish={handleTourClose}
        step={1}
        stepsTotal={1}
        repositionOnScroll={true}
        title={formatMessage(messages.tourTitle)}
        content={
          <EuiFlexGroup
            responsive={false}
            alignItems='top'
            justifyContent='flexStart'
            gutterSize='m'
          >
            <EuiFlexItem grow={false}>
              <EuiIcon type='cheer' size='m' />
            </EuiFlexItem>
            <EuiFlexItem>
              <EuiText size='s'>{formatMessage(messages.tourContent)}</EuiText>
            </EuiFlexItem>
          </EuiFlexGroup>
        }
        anchorPosition='rightCenter'
        zIndex={2000}
      >
        <EuiSideNav
          items={navItemsWithSelected}
          renderItem={NavLinkItem}
          isOpenOnMobile={isSideNavOpenOnMobile}
          toggleOpenOnMobile={toggleOpenMobile}
          mobileTitle={formatMessage(messages.management)}
        />
      </EuiTourStep>
    </EuiCollapsibleNavBeta.Footer>
  )
}

function getOrganizationItems({
  formatMessage,
  organizationId,
  isLoadingPermissions,
  hasViewAccountTabPermission,
  hasNoPermissionsToSeeTabs,
  hasViewMembersTabPermission,
  hasViewApiKeysTabPermission,
  hasAccessToSecurityTab,
}) {
  if (hasNoPermissionsToSeeTabs || !organizationId || isLoadingPermissions) {
    return []
  }

  const items: EuiSideNavItem[] = []

  if (organizationId && hasViewMembersTabPermission) {
    items.push(
      createItem({
        name: formatMessage(messages.accountMembers),
        depth: 2,
        href: accountMembersUrl(),
        [`data-test-id`]: `account-index-members-link`,
      }),
    )
  }

  if (hasViewAccountTabPermission) {
    items.push(
      createItem({
        name: formatMessage(messages.accountContacts),
        depth: 2,
        href: accountContactsUrl(),
        [`data-test-id`]: `account-index-contacts-link`,
      }),
    )
  }

  if (hasViewApiKeysTabPermission) {
    items.push(
      createItem({
        name: formatMessage(messages.accountApiKeys),
        href: apiKeysUrl(),
        depth: 2,
        [`data-test-id`]: `deployment-features-index-api-keys-link`,
      }),
    )
  }

  if (hasAccessToSecurityTab && organizationId) {
    items.push(
      createItem({
        name: formatMessage(messages.security),
        depth: 2,
        href: organizationSecurityUrl(),
        [`data-test-id`]: `account-index-idp-link`,
      }),
    )
  }

  return items
}

function accessSecurityNavigation({ formatMessage, depth }): EuiSideNavItem[] {
  const featuresNavigationItems: EuiSideNavItem[] = []

  const innerDepth = depth + 1

  featuresNavigationItems.push(...buildNetworkSecurityMenu(innerDepth))

  if (isFeatureActivated(Feature.trafficFiltering)) {
    featuresNavigationItems.push(
      createItem({
        name: formatMessage(messages.accountTrafficFilters),
        href: trafficFiltersUrl(),
        depth: innerDepth,
        [`data-test-id`]: `deployment-features-index-traffic-filters-link`,
      }),
    )
  }

  if (isFeatureActivated(Feature.crossEnvCCSCCR)) {
    featuresNavigationItems.push(
      createItem({
        name: formatMessage(messages.trustManagement),
        href: trustManagementUrl(),
        depth: innerDepth,
        [`data-test-id`]: `deployment-features-index-trust-management-link`,
      }),
    )
  }

  return featuresNavigationItems
}

function setSelectedItem(
  items: EuiSideNavItem[],
  isSelected: (item: EuiSideNavItem) => boolean,
): EuiSideNavItem[] {
  return items.map(handleItemSelection)

  function handleItemSelection(item: EuiSideNavItem): EuiSideNavItem {
    if (Array.isArray(item.items)) {
      return {
        ...item,
        items: setSelectedItem(item.items, isSelected),
        isSelected: isSelected(item),
      }
    } else if (isSelected(item)) {
      return {
        ...item,
        isSelected: true,
      }
    }

    return item
  }
}

function isDeploymentActive(pathname: string) {
  return matchPath(startingWith(deploymentUrl(`:deploymentId`)), pathname)
}

function isDeploymentExtensionEditActive(pathname: string) {
  return matchPath(deploymentExtensionUrl(`:extensionId`), pathname)
}

function isDeploymentExtensionCreateActive(pathname: string) {
  return matchPath(deploymentExtensionCreateUrl(), pathname)
}
